import { yupResolver } from "@hookform/resolvers/yup";
import { useDisclosure } from "@nextui-org/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { deleteFetcher, getFetcher, postFetcher, putFetcher } from "../../services/api";
import { isResponseErrorObject } from "../../services/helpers";
import useListPic from "../useListPic";
import useSWR from "swr";
import useListCompany from "../useListCompany";
import { useSelectedCompany } from "../useSelectedCompany";
import { supplierValidation } from "../../services/validation/supplier.validation";

export default function useSupplier() {
    // Perusahaan yang dipilih.
    //
    // Memakai hook bersama, bukan useState(null): untuk pengguna non-super-admin
    // nilainya otomatis terisi perusahaan miliknya sendiri begitu data
    // AuthContext tersedia, sedangkan super admin tetap null supaya bisa memilih
    // sendiri. Dengan useState(null), key SWR di bawah ikut null sehingga tidak
    // ada request sama sekali - tabelnya kosong sampai pengguna memilih
    // perusahaannya sendiri secara manual.
    const [selectedCompany, setSelectedCompany] = useSelectedCompany();
    const {isOpen: isOpenModalForm, onOpen: onOpenModalForm, onOpenChange: onOpenChangeModalForm, onClose: onCloseModalForm} = useDisclosure();
    const {isOpen: isOpenModalAlert, onOpenChange: onOpenChangeModalAlert} = useDisclosure();
    const [editId, setEditId] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const { register, handleSubmit, setValue,  reset, watch, control, formState: { errors, isSubmitting, dirtyFields }} = useForm({ resolver: yupResolver(supplierValidation) });
    const { data, isLoading, mutate } = useSWR(selectedCompany ? `/api/company/data-suppliers/${selectedCompany.value}` : null, getFetcher);
    const { dataCompany, isLoadingCompany } = useListCompany()

    const provinceId = watch('provinceId')
    const regencyId = watch('regencyId')
    const districtId = watch('districtId')
    const companyId = watch('companyId')

    const { data: province } = useSWR('/api/location/provinces', getFetcher)
    const { data: regency } = useSWR(provinceId ? `/api/location/cities?provinceId=${provinceId}`: null, getFetcher)
    const { data: district } = useSWR(regencyId ? `/api/location/districts?regencyId=${regencyId}`: null, getFetcher)
    const { data: village } = useSWR(districtId ? `/api/location/villages?districtId=${districtId}`: null, getFetcher)
    const { dataPIC, isLoadingDataPIC, mutatePIC } = useListPic({ companyId });

    function onClickEdit(item) {    
        setEditId(item.id);
        setIsEdit(true);
        reset({
            companyId: item.companyId,
            namaSupplier: item.namaSupplier,
            alamat: item.alamat,
            email: item.email,
            telepon: item.telepon,
            fax: item.fax,
            latitude: item.latitude,
            longitude: item.longitude,
            provinceId: item.provinceId,
            regencyId: item.regencyId,
            districtId: item.districtId,
            villageId: item.villageId, 
            dataPICIds: item?.DataPic?.map(item => item.id),
        });           
        onOpenChangeModalForm();
    }

    function onCloseForm() {
        setEditId(null);
        setIsEdit(false);
        reset({
            companyId: '',
            namaSupplier: '',
            alamat: '',
            email: '',
            telepon: '',
            fax: '',
            latitude: '',
            longitude: '',
            provinceId: '',
            regencyId: '',
            districtId: '',
            villageId: '',
            dataPICIds: [],
        });
        onCloseModalForm()
    }

    function onClickDelete(id) {
        setEditId(id);
        onOpenChangeModalAlert();
    }
    
    async function onSubmitDelete() {
        try {
            await deleteFetcher('/api/company/supplier', editId);
            mutate()
            toast.success('Perusahaan supplier berhasil dihapus!');
        } catch (error) {
            toast.error('Gagal hapus perusahaan supplier!');
        }
    }

    async function onSubmitForm(data) {
        try {
            if (isEdit) {
                const companyId = data.companyId
                delete data.companyId
                const formData = data;
                await putFetcher('/api/company/supplier', editId, formData);
                mutate()
                toast.success('Perusahaan supplier berhasil diubah!');
            } else {
                const companyId = data.companyId
                delete data.companyId
                const formData = data;
                await postFetcher(`/api/company/data-supplier/${companyId}`, formData);
                mutate()
                const company = dataCompany?.data?.find((item) => item.id === companyId)
                setSelectedCompany({label: company.name, value: company.id})
                toast.success('Perusahaan supplier berhasil ditambah!');
            }
            onCloseForm();
        } catch (error) {
            isResponseErrorObject(error.response.data.message)
                ? Object.entries(error.response.data.message).forEach(([key, value]) => {
                    toast.error(value);
                })
                : toast.error(error.response.data.message)
        }
    }

    return {
        modalForm: {
            isOpenModalForm,
            onOpenModalForm,
            onOpenChangeModalForm,
        },
        modalAlert: {
            isOpenModalAlert,
            onOpenChangeModalAlert,
        },
        hookForm: {
            register, 
            handleSubmit, 
            reset, 
            watch,
            control,
            setValue,
            formState: { errors, isSubmitting, dirtyFields }
        },
        dataFetch: {
            data,
            isLoading,
            dataCompany,
            isLoadingCompany,
            dataPIC,
            isLoadingDataPIC,
            mutatePIC,
            province,
            regency,
            district,
            village
        },
        isEdit,
        selectedCompany,
        setSelectedCompany,
        onClickEdit,
        onCloseForm,
        onClickDelete,
        onSubmitDelete,
        onSubmitForm,
    }
}